'use client'

import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import Image from 'next/image'

type HLSPlayerProps = {
  src: string
  className?: string
  autoPlay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
  ariaLabel?: string
  poster?: string
}

const HLSPlayer = ({
  poster = '',
  src,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  controls = true,
  ariaLabel = 'HLS video stream',
}: HLSPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [loading, setLoading] = useState(true)

  // Preload poster
  useEffect(() => {
    if (poster) {
      const img = new window.Image()
      img.src = poster
    }
  }, [poster])

  const initializeHls = (videoElement: HTMLVideoElement, videoSrc: string) => {
    if (Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(videoSrc)
      hls.attachMedia(videoElement)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoElement.play().catch(() => {})
      })
      hls.on(Hls.Events.ERROR, (_, data) => console.error('HLS.js error:', data))
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = videoSrc
      videoElement.addEventListener('loadedmetadata', () => {
        videoElement.play().catch(() => {})
      })
    } else {
      console.error('HLS not supported in this browser.')
    }
  }

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    setLoading(true)
    video.muted = muted

    initializeHls(video, src)
    // Cleanup Hls instance on unmount
    return () => {
      if (video) {
        video.pause()
        video.src = ''
      }
    }
  }, [src, muted])

  return (
    <>
      {loading && poster && (
        <Image
          src={poster}
          alt="Loading poster"
          fill
          className="absolute inset-0 object-cover"
          priority
        />
      )}
      <video
        ref={videoRef}
        className={className}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        controls={controls}
        playsInline
        aria-label={ariaLabel}
        onCanPlayThrough={() => setLoading(false)}
      />
    </>
  )
}

export default HLSPlayer
